import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import classes from "./TimeScheduler.module.css";
import { Box } from "@mui/material";
import TypographyInline from "../Typography/TypographyInline";
import { GREY, ERROR, BRAND } from "../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../utils/constants/UI";
import { initialTimings, WEEK_DAYS_ID } from "../../utils/constants";
import {
  createTimingsForPayload,
  validationErrorMessage,
} from "../../utils/helpers";
import { AutoCompleteCustom } from "../Autocomplete/AutoCompleteCustom";
import IconComponent from "../Icon/IconComponent";
import CustomTooltip from "../Tooltip/CustomTooltip";

// Day labels
const DAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Generate combined time options in 5-min intervals (288 total)
const TIME_STEP_MINUTES = 5;
const COMBINED_TIMES = (() => {
  const list = [];
  for (const period of ["AM", "PM"]) {
    for (const h of [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]) {
      for (let m = 0; m < 60; m += TIME_STEP_MINUTES) {
        const label = `${h}:${String(m).padStart(2, "0")} ${period}`;
        list.push({ id: label, label });
      }
    }
  }
  return list;
})();

// Convert data fields to combined display string
const toCombined = (hour, minute, type) => {
  const m = Math.floor(minute / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
  return `${hour}:${String(m).padStart(2, "0")} ${type}`;
};

// Parse combined string back to data fields
const fromCombined = (str) => {
  const match = str?.match(/^(\d+):(\d+)\s+(AM|PM)$/);
  if (!match) return null;
  return {
    hour: parseInt(match[1]),
    minute: parseInt(match[2]),
    type: match[3],
  };
};

const TimeScheduler = ({ payload, getData = () => {} }) => {
  // Fully controlled: the parent owns the timings data. We derive `timings`
  // straight from `payload` every render and emit every mutation up through
  // getData synchronously inside the handler. No internal copy, no sync
  // effects, no ref guard -> no second source of truth that can desync, and
  // no deferred passive effect that can re-emit a stale array in a later
  // (un-batched, React 17) commit. This removes the controlled/uncontrolled
  // anti-pattern behind the select-all / reselect-blank and collapse-on-tick
  // bugs.
  const timings = useMemo(
    () =>
      Array.isArray(payload) && payload.length ? payload : [...initialTimings],
    [payload],
  );
  const [timeItems, setTimeItems] = useState(COMBINED_TIMES);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const addMenuRef = useRef(null);
  const addLinkRef = useRef(null);
  const menuListRef = useRef(null);

  // Close add menu on outside click
  useEffect(() => {
    if (!showAddMenu) return;
    const handleClickOutside = (e) => {
      // QA reporter: clicks inside the report dialog/button must not close popovers
      if (e.target?.closest?.("[data-qa-exclude]")) return;
      const insideTrigger =
        addMenuRef.current && addMenuRef.current.contains(e.target);
      const insideMenu =
        menuListRef.current && menuListRef.current.contains(e.target);
      if (!insideTrigger && !insideMenu) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddMenu]);

  // Position the menu using fixed coordinates so it escapes any scroll/overflow clipping.
  // Flips horizontally/vertically if it would overflow the viewport.
  useEffect(() => {
    if (!showAddMenu) {
      setMenuPos(null);
      return;
    }
    const GAP = 4;
    const MARGIN = 8;
    const updatePos = () => {
      const trigger = addLinkRef.current;
      const menuEl = menuListRef.current;
      if (!trigger || !menuEl) return;
      const triggerRect = trigger.getBoundingClientRect();
      const menuW = menuEl.offsetWidth;
      const menuH = menuEl.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Vertical: prefer above the trigger; flip below if not enough room above.
      const spaceAbove = triggerRect.top;
      const spaceBelow = vh - triggerRect.bottom;
      const openAbove = spaceAbove >= menuH + GAP || spaceAbove >= spaceBelow;
      const top = openAbove
        ? Math.max(MARGIN, triggerRect.top - menuH - GAP)
        : Math.min(vh - menuH - MARGIN, triggerRect.bottom + GAP);

      // Horizontal: prefer aligning to trigger's left edge; flip to right edge if it would clip.
      let left = triggerRect.left;
      if (left + menuW + MARGIN > vw) {
        left = Math.max(MARGIN, triggerRect.right - menuW);
      }
      if (left < MARGIN) left = MARGIN;

      setMenuPos({ top, left });
    };
    // Measure after menu mounts. rAF gives the browser a frame to lay out the hidden element.
    const raf = requestAnimationFrame(updatePos);
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [showAddMenu]);

  const activeDays = timings
    .filter((t) => t.checked)
    .sort((a, b) => a.day - b.day);
  const inactiveDays = timings
    .filter((t) => !t.checked)
    .sort((a, b) => a.day - b.day);

  // Validation
  const validate = useCallback((updated) => {
    const changed = createTimingsForPayload(updated);
    const errors = validationErrorMessage(changed);
    const msgMap = {};
    errors?.forEach((item) => {
      msgMap[WEEK_DAYS_ID[item.day]] = item.message;
    });
    updated.forEach((item) => {
      item.message = msgMap[item.day] || "";
    });
    return [...updated];
  }, []);

  // Validate then push the next array up to the parent synchronously. Single
  // emit path for every mutation; runs inside the click handler so the update
  // is batched with no chance of a deferred stale re-emit in a later commit.
  const commit = useCallback(
    (updated) => {
      getData(validate(updated));
    },
    [getData, validate],
  );

  // Time search handler (shared across all time dropdowns)
  const handleTimeSearch = (event) => {
    const q = event?.target?.value?.toLowerCase() || "";
    if (q) {
      setTimeItems(
        COMBINED_TIMES.filter((t) => t.label.toLowerCase().includes(q)),
      );
    } else {
      setTimeItems(COMBINED_TIMES);
    }
  };

  // Add a day
  const addDay = (dayIndex) => {
    const ref = activeDays[0];
    const updated = timings.map((t) => {
      if (t.day === dayIndex) {
        return {
          ...t,
          checked: true,
          // Copy time from first active day, or keep defaults
          ...(ref
            ? {
                startTime: ref.startTime,
                startMinute: ref.startMinute,
                startType: ref.startType,
                endTime: ref.endTime,
                endMinute: ref.endMinute,
                endType: ref.endType,
              }
            : {}),
        };
      }
      return t;
    });
    commit(updated);
  };

  // Remove a day
  const removeDay = (dayIndex) => {
    const updated = timings.map((t) =>
      t.day === dayIndex ? { ...t, checked: false } : t,
    );
    commit(updated);
  };

  // Preset handlers
  const applyPreset = (dayIndices) => {
    const ref = activeDays[0] || initialTimings[1]; // fallback to Mon defaults
    const updated = timings.map((t) => {
      if (dayIndices.includes(t.day)) {
        return {
          ...t,
          checked: true,
          startTime: ref.startTime,
          startMinute: ref.startMinute,
          startType: ref.startType,
          endTime: ref.endTime,
          endMinute: ref.endMinute,
          endType: ref.endType,
        };
      }
      return t;
    });
    commit(updated);
  };

  // Time change handler
  const handleTimeChange = (dayIndex, field, e) => {
    setTimeItems(COMBINED_TIMES); // Reset search for next dropdown
    const str = Array.isArray(e?.label) ? e.label[0] : e?.label;
    const parsed = fromCombined(str);
    if (!parsed) return;

    const isStart = field === "start";
    const updated = timings.map((t) => {
      if (t.day === dayIndex) {
        return {
          ...t,
          ...(isStart
            ? {
                startTime: parsed.hour,
                startMinute: parsed.minute,
                startType: parsed.type,
              }
            : {
                endTime: parsed.hour,
                endMinute: parsed.minute,
                endType: parsed.type,
              }),
        };
      }
      return t;
    });
    commit(updated);
  };

  // Check which presets are fully active
  const weekdaysActive = [1, 2, 3, 4, 5].every(
    (d) => timings.find((t) => t.day === d)?.checked,
  );
  const weekendsActive = [0, 6].every(
    (d) => timings.find((t) => t.day === d)?.checked,
  );
  const allActive = timings.every((t) => t.checked);

  return (
    <Box className={classes.root}>
      {/* Active day rows */}
      {activeDays.length > 0 && (
        <Box className={classes.dayRows}>
          {activeDays.map((timing) => {
            const hasError = timing.message?.length > 0;
            return (
              <Box key={timing.day} className={classes.dayRow}>
                <Box className={classes.dayName}>
                  <TypographyInline
                    size="sm"
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.SEMIBOLD}
                    label={DAY_FULL[timing.day]}
                  />
                </Box>

                <span className={classes.connector}>from</span>

                <Box className={classes.timeDropdown}>
                  {(() => {
                    const startVal = toCombined(
                      timing.startTime,
                      timing.startMinute,
                      timing.startType,
                    );
                    return (
                      <AutoCompleteCustom
                        size="xs34"
                        LeftIcon=""
                        items={timeItems}
                        valueState={startVal}
                        idState={[startVal]}
                        onChange={(e) =>
                          handleTimeChange(timing.day, "start", e)
                        }
                        variant={hasError ? "error" : "default"}
                        isSearchEnable={true}
                        handleSearch={handleTimeSearch}
                        dropdownId={`st-${timing.day}`}
                        paperHeight="220px"
                        paperWidth="160px"
                      />
                    );
                  })()}
                </Box>

                <span className={classes.connector}>to</span>

                <Box className={classes.timeDropdown}>
                  {(() => {
                    const endVal = toCombined(
                      timing.endTime,
                      timing.endMinute,
                      timing.endType,
                    );
                    return (
                      <AutoCompleteCustom
                        size="xs34"
                        LeftIcon=""
                        items={timeItems}
                        valueState={endVal}
                        idState={[endVal]}
                        onChange={(e) =>
                          handleTimeChange(timing.day, "end", e)
                        }
                        variant={hasError ? "error" : "default"}
                        isSearchEnable={true}
                        handleSearch={handleTimeSearch}
                        dropdownId={`et-${timing.day}`}
                        paperHeight="220px"
                        paperWidth="160px"
                      />
                    );
                  })()}
                </Box>

                {hasError && (
                  <CustomTooltip title={timing.message}>
                    <Box className={classes.errorMark}>
                      <IconComponent
                        iconLabel={ICON_LABELS.ERROR}
                        color={ERROR.W_500}
                        fontSize="16px"
                      />
                    </Box>
                  </CustomTooltip>
                )}

                <Box
                  className={classes.deleteBtn}
                  onClick={() => removeDay(timing.day)}
                >
                  <IconComponent
                    iconLabel={ICON_LABELS.DELETE_OUTLINE}
                    color={GREY.TERTIARY}
                    fontSize="18px"
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Add section - HubSpot style "+Add hours" */}
      {!allActive && (
        <Box className={classes.addSection} ref={addMenuRef}>
          <Box
            ref={addLinkRef}
            className={classes.addLink}
            onClick={() => setShowAddMenu(!showAddMenu)}
          >
            <TypographyInline
              size="sm"
              color={BRAND.PRIMARY}
              fontWeight={fontWeight.MEDIUM}
              label="+Add hours"
            />
          </Box>

          {showAddMenu && (
            <Box
              ref={menuListRef}
              className={classes.addMenu}
              style={{
                position: "fixed",
                top: menuPos ? `${menuPos.top}px` : "0px",
                left: menuPos ? `${menuPos.left}px` : "0px",
                zIndex: 1500,
                visibility: menuPos ? "visible" : "hidden",
              }}
            >
              {/* Presets */}
              {!allActive && (
                <Box
                  className={classes.addMenuItem}
                  onClick={() => {
                    applyPreset([0, 1, 2, 3, 4, 5, 6]);
                    setShowAddMenu(false);
                  }}
                >
                  <TypographyInline
                    size="sm"
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.MEDIUM}
                    label="Every day"
                  />
                </Box>
              )}
              {!weekdaysActive && (
                <Box
                  className={classes.addMenuItem}
                  onClick={() => {
                    applyPreset([1, 2, 3, 4, 5]);
                    setShowAddMenu(false);
                  }}
                >
                  <TypographyInline
                    size="sm"
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.MEDIUM}
                    label="Mon - Fri"
                  />
                </Box>
              )}
              {!weekendsActive && (
                <Box
                  className={classes.addMenuItem}
                  onClick={() => {
                    applyPreset([0, 6]);
                    setShowAddMenu(false);
                  }}
                >
                  <TypographyInline
                    size="sm"
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.MEDIUM}
                    label="Sat - Sun"
                  />
                </Box>
              )}

              {/* Divider between presets and individual days */}
              {inactiveDays.length > 0 && (
                <Box className={classes.addMenuDivider} />
              )}

              {/* Individual available days */}
              {inactiveDays.map((t) => (
                <Box
                  key={t.day}
                  className={classes.addMenuItem}
                  onClick={() => {
                    addDay(t.day);
                    setShowAddMenu(false);
                  }}
                >
                  <TypographyInline
                    size="sm"
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.REGULAR}
                    label={DAY_FULL[t.day]}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TimeScheduler;
